import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuTruck, LuMapPin, LuPackage, LuChevronRight } from 'react-icons/lu';
import DriverLayout from '../../components/DriverLayout/DriverLayout';
import TripMap from '../../components/TripMap/TripMap';
import { authFetch, updateOrder, updateTruck } from '../../lib/directus';
import styles from './DriverDashboard.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.driftwoodagri.com';

const ACTIVE_STATUSES = ['transport_sourced', 'pending_dispatch', 'pickup_completed', 'in_transit'];

const STATUS_LABEL = {
  transport_sourced: 'Transport sourced',
  pending_dispatch: 'Pending dispatch',
  pickup_completed:  'Pickup completed',
  in_transit:        'In transit',
  delivered:         'Delivered',
};

const NEXT_STATUS = {
  transport_sourced: { value: 'pending_dispatch', label: 'Mark pending dispatch' },
  pending_dispatch:  { value: 'pickup_completed', label: 'Mark pickup completed' },
  pickup_completed:  { value: 'in_transit',       label: 'Mark in transit' },
  in_transit:        { value: 'delivered',         label: 'Mark as delivered' },
};

function pad2(n) { return String(n).padStart(2, '0'); }

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [activeTrip, setActiveTrip]   = useState(null);
  const [stats, setStats]             = useState({ total: 0, active: 0, delivered: 0 });
  const [loading, setLoading]         = useState(true);
  const [updating, setUpdating]       = useState(false);
  const [updateError, setUpdateError] = useState('');

  const loadData = useCallback(() => {
    setLoading(true);
    authFetch(
      `${API_URL}/items/loads?fields=id,load_id,pickup_location,delivery_location,order.id,order.fulfillment_status,order.batch.commodity,order.batch.grade,order.batch.quantity,assigned_truck.id,assigned_truck.truck_id&filter[assigned_driver][user][_eq]=$CURRENT_USER&filter[order][fulfillment_status][_in]=transport_sourced,pending_dispatch,pickup_completed,in_transit,delivered&sort=-date_updated`
    )
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) throw new Error(json?.errors?.[0]?.message || 'Failed to fetch loads');
        return json;
      })
      .then(json => {
        const trips     = json.data || [];
        const active    = trips.filter(t => ACTIVE_STATUSES.includes(t.order?.fulfillment_status));
        const delivered = trips.filter(t => t.order?.fulfillment_status === 'delivered').length;
        setStats({ total: trips.length, active: active.length, delivered });

        if (active.length > 0) {
          const t     = active[0];
          const batch = t.order?.batch || {};
          setActiveTrip({
            id:              t.id,
            loadId:          t.load_id ? `#${t.load_id}` : `#${String(t.id).slice(0, 8).toUpperCase()}`,
            pickup:          t.pickup_location || '-',
            delivery:        t.delivery_location || '-',
            cargo:           batch.commodity ? `${batch.commodity}${batch.grade ? ` (${batch.grade})` : ''}` : '-',
            quantity:        batch.quantity ? `${batch.quantity} MT` : '-',
            truck:           t.assigned_truck?.truck_id || '-',
            assignedTruckId: t.assigned_truck?.id || null,
            orderId:         t.order?.id || null,
            status:          t.order?.fulfillment_status,
          });
        } else {
          setActiveTrip(null);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleStatusUpdate() {
    if (!activeTrip) return;
    const next = NEXT_STATUS[activeTrip.status];
    if (!next) return;
    setUpdating(true);
    setUpdateError('');
    try {
      await updateOrder(activeTrip.orderId, { fulfillment_status: next.value });
      if (next.value === 'delivered' && activeTrip.assignedTruckId) {
        await updateTruck(activeTrip.assignedTruckId, { status: 'Available' });
      }
      loadData();
    } catch (err) {
      setUpdateError(err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  }

  const nextAction = activeTrip ? NEXT_STATUS[activeTrip.status] : null;

  return (
    <DriverLayout>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Dashboard</h1>
        </div>

        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pad2(stats.total)}</p>
            <p className={styles.statLabel}>total trips</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pad2(stats.active)}</p>
            <p className={styles.statLabel}>active</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pad2(stats.delivered)}</p>
            <p className={styles.statLabel}>delivered</p>
          </div>
        </div>

        {loading && <p className={styles.loadingText}>Loading...</p>}

        {!loading && activeTrip && (
          <>
            <p className={styles.sectionTitle}>Current trip</p>
            <div className={styles.tripLayout}>
              <div className={styles.tripPanel}>
                <div className={styles.tripHeader}>
                  <span className={styles.loadId}>{activeTrip.loadId}</span>
                  <span className={styles.statusBadge}>{STATUS_LABEL[activeTrip.status] || activeTrip.status}</span>
                </div>

                <div className={styles.routeRow}>
                  <div className={styles.routeBlock}>
                    <span className={styles.routeLabel}>PICKUP</span>
                    <span className={styles.routeName}>{activeTrip.pickup}</span>
                  </div>
                  <span className={styles.routeArrow}>→</span>
                  <div className={styles.routeBlock}>
                    <span className={styles.routeLabel}>DELIVERY</span>
                    <span className={styles.routeName}>{activeTrip.delivery}</span>
                  </div>
                </div>

                <div className={styles.divider} />

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}><LuPackage size={14} style={{ marginRight: 6 }} />Cargo</span>
                  <span className={styles.infoValue}>{activeTrip.cargo}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Quantity</span>
                  <span className={styles.infoValue}>{activeTrip.quantity}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}><LuTruck size={14} style={{ marginRight: 6 }} />Truck</span>
                  <span className={styles.infoValue}>{activeTrip.truck}</span>
                </div>

                {nextAction && (
                  <>
                    <div className={styles.divider} />
                    {updateError && <p className={styles.errorText}>{updateError}</p>}
                    <button
                      className={styles.statusBtn}
                      onClick={handleStatusUpdate}
                      disabled={updating}
                    >
                      {updating ? 'Updating...' : nextAction.label}
                      <LuChevronRight size={16} />
                    </button>
                  </>
                )}

                <button className={styles.viewBtn} onClick={() => navigate('/trips')}>
                  View all trips
                </button>
              </div>

              <div className={styles.mapArea}>
                <TripMap pickup={activeTrip.pickup} delivery={activeTrip.delivery} />
              </div>
            </div>
          </>
        )}

        {!loading && !activeTrip && (
          <div className={styles.emptyState}>
            <LuMapPin size={32} color="#d1d5db" />
            <p className={styles.emptyText}>No active trip at the moment.</p>
          </div>
        )}
      </div>
    </DriverLayout>
  );
}
